/**
 * Setup Pending Page
 *
 * Shown when a new user's workspace is being created by the webhook.
 * Polls Clerk publicMetadata for the workspaceReady flag.
 *
 * When the webhook completes (or times out), provides:
 * - Auto-navigation to /workspace when ready
 * - "Create workspace manually" fallback if webhook failed
 * - "Try again" to restart polling
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Plus } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@loader/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@loader/ui';

import { useCreateFirstWorkspace, isWorkspaceReady, navigateToWorkspace } from '@/hooks/useCreateFirstWorkspace';
import { FullPageLoader } from '@/components/common';

/** Max polling attempts before showing timeout (30 x 2s = 60s) */
const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 2000;

const searchSchema = z.object({
  timedOut: z.boolean().optional(),
});

export const Route = createFileRoute('/_authenticated/setup-pending')({
  validateSearch: searchSchema,
  component: SetupPendingPage,
});

function SetupPendingPage() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { timedOut: initialTimedOut } = Route.useSearch();

  const [isTimedOut, setIsTimedOut] = useState(initialTimedOut ?? false);
  const [createError, setCreateError] = useState<string | null>(null);
  const pollAttemptsRef = useRef(0);

  const { createWorkspace, isCreating } = useCreateFirstWorkspace({
    onError: (message) => setCreateError(message),
  });

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const workspaceReady = isWorkspaceReady(user);

  // When workspaceReady becomes true, clear stale cache and navigate.
  // removeQueries ensures the resolver fetches fresh data when _withWorkspace mounts
  // (invalidateQueries wouldn't trigger a refetch since the query has no observers here).
  useEffect(() => {
    if (!workspaceReady) return;

    navigateToWorkspace(queryClient, navigate);
  }, [workspaceReady, queryClient, navigate]);

  // Poll Clerk metadata to detect webhook completion
  useEffect(() => {
    if (isTimedOut || workspaceReady) return;

    const interval = setInterval(async () => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(interval);
        setIsTimedOut(true);
        return;
      }
      try {
        await userRef.current?.reload();
      } catch {
        // Silently retry on next interval
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isTimedOut, workspaceReady]);

  const handleRetry = useCallback(() => {
    pollAttemptsRef.current = 0;
    setIsTimedOut(false);
    setCreateError(null);
  }, []);

  // Timed out — show recovery options
  if (isTimedOut) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Setup is taking longer than expected</CardTitle>
            <CardDescription>
              Your workspace is still being prepared. You can wait a bit longer or create one manually.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button onClick={createWorkspace} disabled={isCreating} size="lg" className="w-full">
              {isCreating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create workspace manually
                </>
              )}
            </Button>

            <Button onClick={handleRetry} variant="outline" className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try again
            </Button>

            {createError && <p className="text-center text-sm text-destructive">{createError}</p>}

            <p className="mt-2 text-center text-sm text-muted-foreground">
              If this issue persists, please contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Polling in progress — show spinner
  return <FullPageLoader text="Setting up your workspace..." />;
}
